/**
 * Collection state atoms
 */
import { atom } from 'jotai';

import { foldersStateAtom } from '@/features/folders/state/foldersAtom';
import { classifyCard } from '@/lib/cardClassification';
import {
  getCmc,
  getDisplayImageUris,
  getFullOracleText,
  getManaSourceColors,
  getOracleId,
  getTypeLine,
  LANGUAGE_LABELS,
} from '@/lib/cardUtils';
import { getPrice } from '@/lib/price';
import { getColorSortValue, parseCollectorNumber, RARITY_ORDER } from '@/lib/sortConstants';
import { getFolderPath } from '@/services/folders';
import { fullCardCacheAtom } from '@/state/cardCache';
import { cardClassificationByOracleIdAtom, getClassificationForCard } from '@/state/cardClassification';
import type { CollectionCard, EnrichedCollectionCard, ScryfallCard } from '@/types/card';

// ============================================================================
// Enrichment Helpers
// ============================================================================

/** Compute display CMC (max CMC across faces for multi-faced cards) */
function computeDisplayCmc(card: ScryfallCard): number {
  if (card.card_faces && card.card_faces.length > 1) {
    const faceCmcs = card.card_faces.map((face) => {
      if (!face.mana_cost) return 0;
      const symbols = face.mana_cost.match(/\{([^}]+)\}/g) || [];
      return symbols.reduce((sum, sym) => {
        const inner = sym.slice(1, -1);
        const num = parseInt(inner, 10);
        return sum + (isNaN(num) ? 1 : num);
      }, 0);
    });
    return Math.max(...faceCmcs);
  }
  return getCmc(card) ?? 0;
}

// ============================================================================
// Collection State Atoms
// ============================================================================

export interface CollectionState {
  data: CollectionCard[] | undefined;
  isLoading: boolean;
  error: Error | null;
}

/** Atom holding the raw collection cards - populated by sync hook */
export const collectionStateAtom = atom<CollectionState>({
  data: undefined,
  isLoading: true,
  error: null,
});

export interface EnrichedCollectionState {
  data: EnrichedCollectionCard[] | undefined;
  isLoading: boolean;
  error: Error | null;
  /** Number of cards waiting for cache population */
  pendingCount: number;
}

/**
 * Derived atom that enriches collection cards with Scryfall data and folder paths.
 * Pure synchronous derivation - automatically re-computes when dependencies change.
 * Cards missing from cache are filtered out (will appear when cache populator fetches them).
 */
export const enrichedCollectionAtom = atom<EnrichedCollectionState>((get) => {
  const collectionState = get(collectionStateAtom);
  const foldersState = get(foldersStateAtom);
  const cardCache = get(fullCardCacheAtom);
  const classificationCache = get(cardClassificationByOracleIdAtom);

  // Pass through loading/error states from raw collection
  if (!collectionState.data) {
    return {
      data: undefined,
      isLoading: collectionState.isLoading,
      error: collectionState.error,
      pendingCount: 0,
    };
  }

  // Empty collection
  if (collectionState.data.length === 0) {
    return {
      data: [],
      isLoading: false,
      error: null,
      pendingCount: 0,
    };
  }

  const folderList = foldersState.data ?? [];

  // Enrich cards that are in cache, track how many are missing
  let pendingCount = 0;
  const enriched: EnrichedCollectionCard[] = [];

  for (const collectionCard of collectionState.data) {
    const card = cardCache.get(collectionCard.scryfall_id);
    if (card) {
      const oracleId = getOracleId(card);
      const classification = getClassificationForCard(classificationCache, oracleId) ?? classifyCard(card);
      const cardIsLand = classification.is_land;
      const typeLine = getTypeLine(card) ?? '';
      const oracleText = getFullOracleText(card);
      const landColors = cardIsLand ? getManaSourceColors(oracleText, card.produced_mana, typeLine) : [];
      const manaSourceColors = getManaSourceColors(oracleText, card.produced_mana, cardIsLand ? typeLine : undefined);

      enriched.push({
        ...collectionCard,
        card,
        folder_path: getFolderPath(collectionCard.folder_id, folderList),
        // Classification fields
        classification,
        land_colors: landColors,
        mana_source_colors: manaSourceColors,
        // Pre-computed fields
        price_usd: getPrice(card.prices, 'usd', collectionCard.finish),
        price_eur: getPrice(card.prices, 'eur', collectionCard.finish),
        display_cmc: computeDisplayCmc(card),
        collector_number_sort: parseCollectorNumber(card.collector_number),
        display_image_uris: getDisplayImageUris(card),
        color_sort_value: getColorSortValue(card.color_identity),
        rarity_sort_order: RARITY_ORDER[card.rarity] ?? 0,
        language_label: LANGUAGE_LABELS[collectionCard.language as keyof typeof LANGUAGE_LABELS] ?? collectionCard.language,
        oracle_text_lower: (getFullOracleText(card) ?? '').toLowerCase(),
      });
    } else {
      pendingCount++;
    }
  }

  return {
    data: enriched,
    isLoading: pendingCount > 0,
    error: null,
    pendingCount,
  };
});

/**
 * Derived atom that maps scryfall_id to total owned quantity.
 * Useful for EditionBrowser to show which printings are owned.
 */
export const collectionOwnedByScryfallIdAtom = atom<Map<string, number>>((get) => {
  const collectionState = get(collectionStateAtom);
  const owned = new Map<string, number>();

  if (!collectionState.data) {
    return owned;
  }

  for (const card of collectionState.data) {
    const current = owned.get(card.scryfall_id) ?? 0;
    owned.set(card.scryfall_id, current + card.quantity);
  }

  return owned;
});
